import React from "react";
import { FaCrown, FaCheck, FaWhatsapp, FaArrowRight } from "react-icons/fa6";

/* ─── Colour tokens per badge type ─────────────────────────── */
const SCHEME = {
  gold:    { accent: "#fbbf24", glow: "rgba(251,191,36,0.20)",  ring: "rgba(251,191,36,0.25)" },
  red:     { accent: "#f87171", glow: "rgba(248,113,113,0.20)", ring: "rgba(248,113,113,0.25)" },
  green:   { accent: "#34d399", glow: "rgba(52,211,153,0.20)",  ring: "rgba(52,211,153,0.25)" },
  purple:  { accent: "#c084fc", glow: "rgba(192,132,252,0.20)", ring: "rgba(192,132,252,0.25)" },
  blue:    { accent: "#60a5fa", glow: "rgba(96,165,250,0.20)",  ring: "rgba(96,165,250,0.25)" },
  default: { accent: "#94a3b8", glow: "rgba(148,163,184,0.14)", ring: "rgba(148,163,184,0.22)" },
};

function PricingBox({
  title,
  subtitle,
  price,
  duration,
  effectivePrice,
  badge,
  badgeType = "gold",
  isFeatured = false,
  features = [],
  ctaText = "Join This Plan",
  whatsappMessage,
}) {
  const defaultWaMsg = `Hi Coach Nandha, I want to join FAT2FIT under the ${title} package (Rs.${price})! Please guide me with enrollment.`;
  const encodedMsg = encodeURIComponent(whatsappMessage || defaultWaMsg);
  const s = SCHEME[badgeType] || SCHEME.default;

  return (
    <div
      className={`pb-card${isFeatured ? " pb-card--featured" : ""}`}
      style={{
        "--accent": s.accent,
        "--glow":   s.glow,
        "--ring":   s.ring,
      }}
    >
      {/* ── Top accent bar ─────────────────────── */}
      <div className="pb-topbar" />

      {/* ── Corner glow ────────────────────────── */}
      <div className="pb-corner-glow" />

      {/* ── "MOST POPULAR" ribbon (featured only) ─ */}
      {isFeatured && (
        <div className="pb-ribbon flex items-center gap-1.5">
          <FaCrown className="text-[10px]" />
          <span>MOST POPULAR</span>
        </div>
      )}

      {/* ════════════ HEADER ════════════ */}
      <div className="pb-header">
        {/* Badge + effective-price row */}
        <div className="pb-badge-row">
          <span className="pb-badge">
            <span className="pb-badge-dot" />
            {badge || "Package"}
          </span>
          {effectivePrice && (
            <span className="pb-effective">{effectivePrice}</span>
          )}
        </div>

        {/* Plan name */}
        <h3 className={`pb-title${isFeatured ? " pb-title--featured" : ""}`}>
          {title}
        </h3>
        {subtitle && <p className="pb-subtitle">{subtitle}</p>}
      </div>

      {/* ════════════ PRICE BLOCK ════════════ */}
      <div className="pb-price-wrap">
        <div className="pb-price-box">
          <div className="pb-price-left">
            <span className="pb-currency">₹</span>
            <span className="pb-amount">{price}</span>
          </div>
          <div className="pb-price-right">
            <span className="pb-duration">{duration}</span>
            <span className="pb-incl">incl. coach</span>
          </div>
        </div>
      </div>

      {/* ════════════ FEATURES ════════════ */}
      <div className="pb-features">
        <p className="pb-features-label">
          <span className="pb-label-line" />
          What&apos;s Included
        </p>
        <ul className="pb-list">
          {features.map((feat, idx) => (
            <li key={idx} className="pb-list-item">
              <span className="pb-check">
                <FaCheck className="text-[8px]" />
              </span>
              <span className="pb-feat-text">{feat}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* ════════════ CTA ════════════ */}
      <div className="pb-cta-wrap">
        <a
          href={`https://wa.me/918637488702?text=${encodedMsg}`}
          target="_blank"
          rel="noreferrer"
          className={`pb-cta${isFeatured ? " pb-cta--featured" : ""}`}
        >
          <span className="pb-shine" />
          <FaWhatsapp className="pb-cta-icon text-[16px]" />
          <span className="pb-cta-label">{ctaText}</span>
          <FaArrowRight className="pb-cta-arrow text-[11px]" />
        </a>
      </div>

      {/* ════════════ SCOPED CSS ════════════ */}
      <style>{`
        /* ── Card shell ─────────────────────────────────────────── */
        .pb-card {
          position: relative;
          display: flex;
          flex-direction: column;
          width: 100%;
          background: #0c0c12;
          border: 1px solid #1d1d28;
          border-radius: 18px;
          overflow: hidden;
          transition: transform .4s ease, box-shadow .4s ease, border-color .4s ease;
          box-shadow: 0 4px 30px rgba(0,0,0,.55);
        }
        .pb-card:hover {
          transform: translateY(-5px);
          border-color: var(--ring);
          box-shadow: 0 16px 50px rgba(0,0,0,.65), 0 0 0 1px var(--ring);
        }
        .pb-card--featured {
          background: linear-gradient(160deg,#12111e 0%,#0c0c12 55%,#090910 100%);
          border: 1.5px solid var(--accent);
          box-shadow: 0 0 0 1px var(--ring), 0 20px 60px var(--glow), 0 4px 24px rgba(0,0,0,.7);
          transform: scale(1.02);
          z-index: 10;
        }
        .pb-card--featured:hover {
          transform: scale(1.03) translateY(-4px);
          box-shadow: 0 0 0 1px var(--ring), 0 28px 80px var(--glow), 0 4px 28px rgba(0,0,0,.8);
        }

        /* ── Top accent bar ─────────────────────────────────────── */
        .pb-topbar {
          height: 3px;
          width: 100%;
          flex-shrink: 0;
          background: linear-gradient(90deg, transparent, var(--accent), transparent);
        }

        /* ── Corner ambient glow ────────────────────────────────── */
        .pb-corner-glow {
          position: absolute;
          top: 0; right: 0;
          width: 180px; height: 180px;
          border-radius: 50%;
          background: radial-gradient(circle at top right, var(--glow), transparent 70%);
          pointer-events: none;
          z-index: 0;
        }

        /* ── Featured ribbon ────────────────────────────────────── */
        .pb-ribbon {
          position: absolute;
          top: 20px;
          right: -28px;
          background: linear-gradient(135deg, var(--accent), #fff9c4 50%, var(--accent));
          color: #000;
          font-size: 9px;
          font-weight: 900;
          letter-spacing: .18em;
          text-transform: uppercase;
          padding: 5px 42px;
          transform: rotate(40deg);
          z-index: 30;
          box-shadow: 0 2px 12px var(--glow);
          white-space: nowrap;
        }

        /* ── Header ─────────────────────────────────────────────── */
        .pb-header {
          position: relative;
          z-index: 10;
          padding: 22px 22px 0;
        }
        .pb-badge-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 14px;
        }
        .pb-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: .14em;
          text-transform: uppercase;
          color: var(--accent);
          background: color-mix(in srgb, var(--accent) 12%, transparent);
          border: 1px solid color-mix(in srgb, var(--accent) 28%, transparent);
          border-radius: 999px;
          padding: 4px 12px;
        }
        .pb-badge-dot {
          width: 6px; height: 6px;
          border-radius: 50%;
          background: var(--accent);
          box-shadow: 0 0 7px var(--accent);
          flex-shrink: 0;
        }
        .pb-effective {
          font-size: 11px;
          font-weight: 700;
          color: #a1a1aa;
          background: #181820;
          border: 1px solid #2a2a36;
          border-radius: 999px;
          padding: 3px 10px;
        }
        .pb-title {
          font-size: clamp(19px, 2vw, 22px);
          font-weight: 900;
          letter-spacing: -.02em;
          text-transform: uppercase;
          line-height: 1.1;
          color: #f4f4f5;
          margin: 0;
        }
        .pb-title--featured {
          background: linear-gradient(135deg,#fff,var(--accent),#fff);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .pb-subtitle {
          font-size: 12px;
          color: #52525b;
          margin: 4px 0 0;
          font-weight: 500;
          line-height: 1.5;
        }

        /* ── Price block ────────────────────────────────────────── */
        .pb-price-wrap {
          position: relative;
          z-index: 10;
          padding: 16px 22px;
        }
        .pb-price-box {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          background: #101018;
          border: 1px solid color-mix(in srgb, var(--accent) 15%, transparent);
          border-radius: 12px;
          padding: 14px 16px;
        }
        .pb-price-left {
          display: flex;
          align-items: flex-end;
          gap: 3px;
        }
        .pb-currency {
          font-size: 19px;
          font-weight: 900;
          color: var(--accent);
          padding-bottom: 5px;
          line-height: 1;
        }
        .pb-amount {
          font-size: clamp(36px, 4.5vw, 44px);
          font-weight: 900;
          color: #f4f4f5;
          letter-spacing: -.04em;
          line-height: 1;
        }
        .pb-price-right {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
        }
        .pb-duration {
          font-size: 11px;
          font-weight: 700;
          color: #71717a;
          letter-spacing: .04em;
        }
        .pb-incl {
          font-size: 9px;
          font-weight: 600;
          color: #3a3a46;
          margin-top: 2px;
          text-transform: uppercase;
          letter-spacing: .1em;
        }

        /* ── Features ───────────────────────────────────────────── */
        .pb-features {
          position: relative;
          z-index: 10;
          padding: 0 22px 18px;
        }
        .pb-features-label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 9px;
          font-weight: 800;
          letter-spacing: .22em;
          text-transform: uppercase;
          color: #3a3a46;
          margin: 0 0 12px;
        }
        .pb-label-line {
          background: var(--accent);
          width: 12px;
          height: 1.5px;
          display: inline-block;
          flex-shrink: 0;
        }
        .pb-list {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 9px;
        }
        .pb-list-item {
          display: flex;
          align-items: flex-start;
          gap: 10px;
        }
        .pb-check {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: color-mix(in srgb, var(--accent) 12%, transparent);
          border: 1px solid color-mix(in srgb, var(--accent) 28%, transparent);
          color: var(--accent);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 8px;
          flex-shrink: 0;
          margin-top: 1px;
        }
        .pb-feat-text {
          font-size: 13px;
          color: #a1a1aa;
          line-height: 1.45;
          font-weight: 500;
          transition: color .2s;
        }
        .pb-card:hover .pb-feat-text {
          color: #d4d4d8;
        }

        /* ── CTA ────────────────────────────────────────────────── */
        .pb-cta-wrap {
          position: relative;
          z-index: 10;
          padding: 0 22px 22px;
        }
        .pb-cta {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          padding: 13px 20px;
          border-radius: 11px;
          font-size: 11.5px;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: .12em;
          text-decoration: none;
          position: relative;
          overflow: hidden;
          transition: all .32s cubic-bezier(.4,0,.2,1);
          /* default state */
          background: #111120;
          color: #71717a;
          border: 1px solid color-mix(in srgb, var(--accent) 28%, transparent);
        }
        .pb-cta:hover {
          background: var(--accent);
          color: #000;
          border-color: var(--accent);
          box-shadow: 0 8px 28px var(--glow);
          transform: translateY(-1px);
          letter-spacing: .16em;
        }
        .pb-cta--featured {
          background: linear-gradient(135deg, var(--accent) 0%, #fff9c4 50%, var(--accent) 100%);
          color: #000;
          border: none;
          box-shadow: 0 8px 30px var(--glow);
        }
        .pb-cta--featured:hover {
          filter: brightness(1.08);
          transform: translateY(-2px);
          color: #000;
          box-shadow: 0 12px 40px var(--glow);
        }
        /* shine sweep */
        .pb-shine {
          position: absolute;
          inset: 0;
          background: linear-gradient(105deg, transparent 40%, rgba(255,255,255,.22) 50%, transparent 60%);
          transform: translateX(-110%);
          transition: transform .55s ease;
          pointer-events: none;
        }
        .pb-cta:hover .pb-shine { transform: translateX(110%); }
        /* icon & arrow */
        .pb-cta-icon { font-size: 16px; position: relative; z-index:1; }
        .pb-cta-label { position: relative; z-index:1; }
        .pb-cta-arrow { font-size: 11px; position: relative; z-index:1; transition: transform .25s; }
        .pb-cta:hover .pb-cta-arrow { transform: translateX(4px); }
      `}</style>
    </div>
  );
}

export default PricingBox;